// Content for the AI Services / approach page. Hand-authored (not Contentful-
// driven), mirroring how AiJourney keeps its copy in a local data file.

// PARKED - the business-case bridge these four points powered was replaced by
// the partners strip (see PartnersSection in index.js). Kept because the copy
// is still good and we may reintroduce it further down the page; nothing
// renders it right now.
export const POINTS = [
  {
    lead: 'Frugal by design is cheaper by default.',
    body: 'The efficient setup - right-sized models, lean architecture, the smallest model that does the job - is both the low-emission one and the low-cost one. Your sustainability lead and your CFO are asking for the same thing without knowing it.',
  },
  {
    lead: 'AI only pays off when people actually use it.',
    body: 'Value doesn’t come from buying the tool; it comes from adoption. Do it with people and it gets used - and used is where the return lives. Doing it humanely isn’t the soft option, it’s the ROI.',
  },
  {
    lead: 'Targeting real work beats “AI everywhere.”',
    body: 'Prioritising the genuinely useful use cases means less compute burned on gimmicks and more benefit that shows up - lower footprint and higher value, same decision.',
  },
  {
    lead: 'Trust is a productivity feature.',
    body: 'Clear rules and honest framing unblock the hesitant middle; usage goes up and risk goes down at once.',
  },
]

// The six services.
// `color` is UNUSED as of the light-card restyle: the chips and the blocks both
//   run on one purple now, and each service is identified by its icon (mapped
//   by id in index.js) instead of its own accent. Kept in case we bring
//   per-service colour back somewhere - nothing renders it today.
// `fx` was never implemented and no job defines it.
// `media` labels the placeholder box, shown only until a service has an `image`.
// `tag` and `outcome` are also UNUSED now: the line above each block headline is
//   the icon + `q`, and the outcome box was replaced by a Book a call button.
//   Copy kept in place rather than deleted.
export const JOBS = [
  {
    id: 'start',
    // adds the community-survey ghost button next to Book a call in this
    // block's CTA row. Only this service has it - flip it here rather than in
    // index.js if it should appear elsewhere.
    showSurveyCta: true,
    color: '#8c4cf6',
    tag: 'People-first',
    label: 'Getting started',
    // `media` is the placeholder label, used only while `image` is absent.
    media: 'Workshop templates',
    // Pre-optimised in static/images/ai-services: webp at 555/740/1110 plus a
    // png fallback. width/height are the intrinsic pixels, for aspect ratio.
    image: {
      src: '/images/ai-services/svc-start',
      width: 1135,
      height: 1026,
      widths: [555, 740, 1110],
      defaultWidth: 740,
      alt: 'An LFCA workshop board: the team dot-voting on an emotion wheel to say how AI makes them feel, with participants on video alongside',
    },
    q: 'Where do we even begin with AI?',
    title: 'Get started - the honest way',
    body: [
      'Most teams freeze at the start: too many tools, too much hype, and a quiet worry about who’s already ahead. So we don’t begin with software - we begin with people.',
      'In a guided workshop your team surfaces how they actually feel, maps where AI could genuinely help their real work, and picks the first pilots themselves. Momentum comes from within, not from a mandate.',
    ],
    includes: [
      'Emotion-wheel conversation starter',
      'Use-case mapping (impact × effort)',
      'First pilots chosen by the team',
      'A shared starting language',
    ],
    outcome:
      'A team that’s aligned, honest about where it stands, and already moving on two or three real pilots.',
  },
  {
    id: 'stall',
    // largest variant is 1039 because that is the source's native width - the
    // srcset never upscales
    image: {
      src: '/images/ai-services/svc-stall',
      width: 1039,
      height: 957,
      widths: [555, 740, 1039],
      defaultWidth: 740,
      alt: 'A readiness check on a phone - "In your team, using AI openly is..." with options from encouraged to frowned upon - beside a shared tracker of people and their status',
    },
    color: '#3d68f0',
    tag: 'People-first',
    label: 'Beyond the keen few',
    media: 'Readiness dashboard',
    q: 'Why are we seeing low AI adoption?',
    title: 'Get past the first adopters',
    body: [
      'The enthusiasts race ahead, and then… silence. The rest aren’t against AI - they’re unsure it’s for them, unsure it’s allowed, reluctant to admit they feel behind.',
      'We find out exactly why, team by team, with a readiness assessment and diagnostic - then run tailored sessions that meet each group where they are and get them moving.',
    ],
    includes: [
      'Readiness assessment',
      'Blocker diagnostic, by team',
      'Tailored engagement sessions',
      'A plan to move the quiet middle',
    ],
    outcome:
      'Adoption that spreads past the first keen 20% and into the majority - where the return actually lives.',
  },
  {
    id: 'deep',
    // source is 2028px wide; capped at 1200 because the media column renders at
    // ~605px CSS, so 1200 already covers 2x. width/height are the capped pair -
    // they only carry the aspect ratio.
    image: {
      src: '/images/ai-services/svc-deep',
      width: 1200,
      height: 1055,
      widths: [555, 740, 1200],
      defaultWidth: 740,
      alt: 'A real working session on a laptop: an app\u2019s library page open in the browser while an AI assistant runs the dev server and grabs a screenshot on request',
    },
    color: '#ffa0eb',
    tag: 'People-first',
    label: 'Real productivity',
    media: 'Playbook sample',
    q: 'Is the productivity actually there?',
    title: 'Go deep in one function',
    body: [
      'Generic training rarely survives contact with real work. So we go deep in a single function and build from your team’s actual tasks - not a demo dataset.',
      'You get playbooks and workflows drawn from what your people really do, like our AI for Sustainability playbook, with the before-and-after measured so the productivity isn’t a claim - it’s a number.',
    ],
    includes: [
      'Task mapping for the function',
      'Ready-to-run workflows & prompts',
      'A function playbook',
      'Before / after read-out',
    ],
    outcome:
      'Proven, measured gains in one function you can point to - and a template you can repeat.',
  },
  {
    id: 'impact',
    image: {
      src: '/images/ai-services/svc-impact',
      width: 1182,
      height: 1158,
      widths: [555, 740, 1182],
      defaultWidth: 740,
      alt: 'An AI supply-chain impact framework on a shared board: upstream stages mapped against affected stakeholders - nature, workers, communities, water, climate - beside the framework cover',
    },
    color: '#ffc839',
    tag: 'Planet-first',
    label: 'Our AI’s impact',
    media: 'Impact report',
    q: 'What is our AI costing the planet?',
    title: 'Answer the impact questions',
    body: [
      'Provider dashboards tell you almost nothing about your real footprint. The number a vendor gives you is a floor, not the full picture.',
      'We build an impact assessment from your actual usage - energy, water, embedded hardware and the parts vendors leave out - so you can answer honestly and early, before your sustainability lead has to raise the alarm.',
    ],
    includes: [
      'Assessment from real usage',
      'The full footprint, not just CO₂',
      'Defensible method & benchmarks',
      'Report-ready wording',
    ],
    outcome:
      'A credible, first-mover answer to “what’s our AI impact?” - not a scramble when someone finally asks.',
  },
  {
    id: 'rightsize',
    image: {
      src: '/images/ai-services/svc-rightsize',
      width: 1139,
      height: 1042,
      widths: [555, 740, 1139],
      defaultWidth: 740,
      alt: 'A shared board being worked through live: numbered chapters beside best-practice grids for embedding sustainability into product routines',
    },
    color: '#5119bc',
    tag: 'Planet-first',
    label: 'Footprint & bill',
    media: 'Right-sizing plan',
    q: 'How do we shrink the footprint - and the bill?',
    title: 'Right-size the setup',
    body: [
      'Here’s the part most people miss: the efficient setup is the low-impact one and the low-cost one. It’s the same decision, pulled once.',
      'We give you a right-sizing plan across model choice, architecture and vendor setup that cuts emissions and spend together - so your CFO and your sustainability lead finally want the same thing.',
    ],
    includes: [
      'Model right-sizing',
      'Architecture review',
      'Vendor & contract setup',
      'A prioritised action list',
    ],
    outcome:
      'A lower footprint and a smaller bill - from the very same set of changes.',
  },
  {
    id: 'rules',
    // source is 1378px wide; capped at 1200, which already covers 2x for a
    // ~605px column
    image: {
      src: '/images/ai-services/svc-rules',
      width: 1200,
      height: 1085,
      widths: [555, 740, 1200],
      defaultWidth: 740,
      alt: 'An open-source AI governance and security baseline document open in a repo, being read through together',
    },
    color: '#2c1a5e',
    tag: 'Rules that stick',
    label: 'Rules & governance',
    media: 'Policy & register',
    q: 'How do we set rules without a compliance team?',
    title: 'The governance kit',
    body: [
      'You don’t need a legal department to use AI responsibly - you need clear, living rules that people actually follow.',
      'Our governance kit is sized for teams with no compliance function: a tool register, an internal policy that reads like your team talks, and a plain-language guide to what the EU AI Act actually asks of you.',
    ],
    includes: [
      'Tool register',
      'Readable internal AI policy',
      'EU AI Act, in plain words',
      'Green / amber / red guardrails',
    ],
    outcome:
      'Rules that are understood, followed and lived in the everyday - not filed away and forgotten.',
  },
]

// Partner / client logos for the short trust strip in section 2. Files live in
// static/logos/partners as pre-optimised webp (no png fallback); `width` and
// `height` are the intrinsic pixel sizes so the browser can reserve space and
// avoid layout shift. `defaultWidth` is which variant the plain src points at,
// for anything that ignores srcset. `variant` decides the optical cap in styles.less -
// wordmarks read heavier than a square app icon at the same height.
// NOTE(timo): confirm the official name for `sustainable-ai-eu` - it is taken
// from the icon artwork alone, so the alt text is a best guess.
export const PARTNERS = [
  {
    id: 'zattoo',
    name: 'Zattoo',
    file: 'zattoo',
    width: 274,
    height: 76,
    variant: 'wordmark',
  },
  {
    id: 'ecosia',
    name: 'Ecosia',
    file: 'ecosia',
    width: 248,
    height: 64,
    variant: 'wordmark',
  },
  {
    id: 'sustainable-ai-eu',
    name: 'Sustainable AI EU',
    file: 'sustainable-ai-eu',
    width: 200,
    height: 200,
    variant: 'icon',
  },
  {
    id: 'coalition-sustainable-ai',
    name: 'Coalition for Sustainable AI',
    file: 'coalition-sustainable-ai',
    width: 322,
    height: 64,
    variant: 'wordmark',
  },
]

// Closing "how we work" section: four principles, each with the concrete
// artefacts a client actually receives.
// NOTE(timo): two copy edits you should check -
//   1. `impact` originally ended "...and recommendations for" mid-sentence. I
//      closed it with "recommendations you can act on" - replace with what you
//      meant.
//   2. `rules` has only one item in `seeItIn`; the others have two or three, so
//      that column's list looks thin. Worth a second and third.
export const PRINCIPLES = [
  {
    id: 'people',
    title: 'People first, not tools first',
    body: 'Most AI rollouts are done to teams. We start with how yours actually works and let them co-create the first uses.',
    seeItIn: ['Team survey', 'Workshop', 'Adoption check'],
  },
  {
    id: 'realwork',
    title: 'Real work in real teams, not \u201cAI everywhere\u201d',
    body: 'We find the tasks eating your team\u2019s week and build for those.',
    seeItIn: ['Task audit', 'Build week'],
  },
  {
    id: 'planet',
    title: 'Honest about the planet',
    body: 'Feels like nobody\u2019s counting what AI costs the planet. We do, with a lean method you can defend and recommendations you can act on.',
    seeItIn: ['AI footprint estimator', 'Vendor questions'],
  },
  {
    id: 'rules',
    title: 'Rules people can follow and live in your org',
    body: 'Clear and digestible policies that build trust and can help you unblock the hesitant middle.',
    seeItIn: ['Tool register'],
  },
]
